import { call, put, takeLatest } from "redux-saga/effects";
import { ProductAPI } from "services/product";
import { convertToThaiFormat, DateFormat_2, DateFormat_1 } from "utils/date";
import { push, replace } from "connected-react-router";
import { getRoutePath, Routes } from "const/routes";
import { openModal } from "redux/reducers/central-modal";
import compact from "lodash/compact";
import {
  getPrimaryProducts,
  getPrimaryProductsSuccess,
  getPrimaryProductsFailure,
  getPrimaryProductDetail,
  getPrimaryProductDetailSuccess,
  getPrimaryProductDetailFailure,
  fetchingProductDetailDone,
  fetchBanner,
  fetchBannerSuccess,
  fetchBannerFailure,
  closeLoadingIndex,
} from "redux/reducers/product";
import { changeUnitDisplaySetting, initSubscription } from "redux/reducers/subscription";
import { CentralModalType, DisclaimerType, ErrorType, UnitDisplay } from "const";
import {
  isBahtDisplay,
  isSecurityTypeDBT,
  isSecurityTypeSB,
  isSecurityTypeSTO,
  unitToBaht,
} from "utils/product-order-util";
import { StepControl } from "utils/misc/step-control";
import { handleClientError } from "utils/saga-error";
import flatten from "lodash/flatten";
import store from "redux/store";
import { PerpBondAPI } from "services/perp-bond";
import { selectPerpBondSuccess, setRequireProductTestModalVisible } from "redux/reducers/perp-bond";
import { isMissingTaxIdError, isSystemCloseError } from "const/const-helper";
import { PreTestSection } from "routes/PerpetualBond/const";
import { shouldDefaultPurchaseUnitWithRemainUnit } from "utils/redeem";
import { getPromise } from "utils/common-util";

const getPrimaryProductsSaga = function* () {
  try {
    const response = yield call(ProductAPI.getPrimaryProducts);
    const securities = response.data?.result?.securities ?? [];
    yield put(getPrimaryProductsSuccess(securities));
  } catch (error) {
    //* tax id validation
    if (isMissingTaxIdError(error?.code)) {
      error.ignore = true;
    }

    if (isSystemCloseError(error?.code)) {
      yield put(getPrimaryProductsSuccess([]));
    }

    yield put(getPrimaryProductsFailure(error));
  }
};

const fetchBannerSaga = function* () {
  try {
    const response = yield call(ProductAPI.fetchAllBanner);
    const banners = response.data?.result?.bannerList ?? [];
    yield put(fetchBannerSuccess(banners));
  } catch (error) {
    yield put(fetchBannerFailure(handleClientError(error)));
  }
};

const getPrimaryProductDetailSaga = function* ({ payload: product }) {
  try {
    const response = yield call(ProductAPI.getProductDetail, { securityCode: product.securityCode });
    yield put(fetchingProductDetailDone());
    const {
      acceptRiskLevel,
      acceptFxRate,
      acceptFutureRisk,
      acceptNoneHNW,
      acceptHNWS,
      acceptHNW,
      acceptVulnerable,
      acceptHNWW,
      acceptHNWE,
      acceptUHNWW,
      acceptUHNWE,
      detail: productDetail,
      lombard: canActiveLombardFlow,
      mncQuotaId,
      accAlertType: globalSavingsAlertType,
      accAlertMessage: globalSavingsAlertMessage,
      sfcdCurrencyCodes: globalSavingsSfcdCurrencyCodes,
    } = response.data.result;

    // convenient field
    productDetail.isSecurityBondConnect = productDetail.productType?.toUpperCase() === "BondCN".toUpperCase();
    const acceptBondConnectPDPA = productDetail.isSecurityBondConnect ? DisclaimerType.BondConnectPDPA : null;
    productDetail.isPremiumDiscount = productDetail.sellParValue !== productDetail.sellPriceUnit;
    productDetail.isTaxApplicable = !!productDetail.hasWht;

    // In case wht is 0, force UI to behave as normal (not as premium-discount with tax).
    if (!productDetail.hasWht) {
      // Removed because this is still premium-discount, just without tax.
      // productDetail.isPremiumDiscount = false;
      productDetail.isTaxApplicable = false;
    }

    // check Global Savings account
    if (globalSavingsAlertType || globalSavingsAlertMessage) {
      const { promise, resolve } = getPromise();
      yield put(
        openModal({
          code: CentralModalType.ValidateDepositAccount,
          state: {
            accAlertType: globalSavingsAlertType,
            accAlertMessage: globalSavingsAlertMessage,
            sfcdCurrencyCodes: globalSavingsSfcdCurrencyCodes,
            source: "PRIMARY",
          },
          actions: {
            onNext: () => {
              resolve(true);
            },
            onCancel: () => {
              resolve(false);
            },
          },
        }),
      );

      const next = yield promise;
      if (!next) {
        yield put(closeLoadingIndex());
        return;
      }
    }

    // check pre test
    if (productDetail.preSubId) {
      const preSubPayloadRequest = {
        preSubId: productDetail.preSubId,
        securityCode: productDetail.securityCode,
      };
      const responseProductTest = yield call(PerpBondAPI.getPerpBondDetail, preSubPayloadRequest);
      const testSuit = responseProductTest.data.result?.preSubTestSecuritiesDetail ?? {};
      const someTestNotDone = [
        testSuit.video?.status,
        testSuit.productTest?.status,
        testSuit.precaution?.status,
        testSuit.ka?.status,
        testSuit.risk?.status,
      ].some((s) => s === false);

      if (someTestNotDone) {
        testSuit.section = PreTestSection.PrimaryMarket;
        yield put(selectPerpBondSuccess(testSuit));
        yield put(setRequireProductTestModalVisible(true));
        return;
      }
    }

    // please preserve order of accepted modal
    const requireAcceptDisclaimerList = compact([
      acceptBondConnectPDPA,
      acceptRiskLevel,
      acceptFxRate,
      acceptFutureRisk,
      acceptHNWS,
      acceptHNW,
      acceptNoneHNW,
      acceptHNWW,
      acceptHNWE,
      acceptUHNWW,
      acceptUHNWE,
      acceptVulnerable,
    ]);
    const disclaimerMapping = {};
    const acceptDiscList = flatten(
      yield new StepControl(requireAcceptDisclaimerList, (code, [next, prev, cancel, forceEnd]) => {
        const backOnCancel = [DisclaimerType.HNW_EXPERIENCE, DisclaimerType.UHNW_EXPERIENCE].includes(code);
        const actions = {
          onAccept: async (value) => {
            if (code === DisclaimerType.BondConnectPDPA) {
              const response = await ProductAPI.ackSubscriptionDisclaimer({
                disclaimerCode: DisclaimerType.BondConnectPDPA,
                version: 1,
              });
              disclaimerMapping[DisclaimerType.BondConnectPDPA] = response.data.result.acceptId;
              next(value);
            } else {
              next(value);
            }
          },
          onCancel: backOnCancel ? prev : forceEnd,
          onClose: () => cancel({ message: `cancel some require disclaimer`, ignore: true }),
        };
        const state = (() => {
          if ([ErrorType.AGE_OVER_60, ErrorType.ATTITUDE, ErrorType.EXPERIENCE].includes(code)) {
            return { ...productDetail };
          }
          if (acceptNoneHNW) {
            return { uhnwSpecial: productDetail.investorType === "HNW002" };
          }
        })();
        store.dispatch(openModal({ code, state, actions }));
      }).start(),
    );

    const acceptedAll = requireAcceptDisclaimerList.every((disc) => acceptDiscList.includes(disc));
    if (acceptedAll) {
      const initSubscriptionPayload = {
        canActiveLombardFlow,
        acceptDiscList: acceptDiscList.filter((disc) => typeof disc === "string"),
        attachDocuments: flatten(
          acceptDiscList.filter((fileArray) => Array.isArray(fileArray) && fileArray.every((file) => file.isFile)),
        ),
        mncQuotaId,
        disclaimerMapping,
      };

      // [START] Default purchase unit
      let defaultPurchaseUnit = productDetail.sellMinUnit;
      const hasQuotaUnit = !!productDetail.quotaUnit;

      if (productDetail?.unitHolders?.length === 1) {
        const unitHolder = productDetail.unitHolders[0];
        initSubscriptionPayload.unitHolder = unitHolder;
        initSubscriptionPayload.acId = unitHolder?.acId;
        if (!hasQuotaUnit && shouldDefaultPurchaseUnitWithRemainUnit(unitHolder)) {
          defaultPurchaseUnit =
            unitHolder.remainUnit > 0
              ? unitHolder.remainUnit
              : productDetail.isDebtIntoCapitalProduct
                ? 0
                : defaultPurchaseUnit;
        }
      }

      if (hasQuotaUnit) {
        defaultPurchaseUnit = productDetail.quotaUnit;
      }

      initSubscriptionPayload.defaultPurchaseUnit = defaultPurchaseUnit;
      initSubscriptionPayload.unit = defaultPurchaseUnit;
      initSubscriptionPayload.baht = unitToBaht(defaultPurchaseUnit, productDetail.sellPriceUnit);
      // [END] Default purchase unit

      // Tier 1
      // check "Tier 2" case for hide unit holders
      // Tier 2 : remain unit is unlimited
      if (productDetail.unitHolders?.[0]?.citizenFlag === "Y") {
        // auto select
        const unitHolder = productDetail.unitHolders[0];
        initSubscriptionPayload.unitHolder = unitHolder;
        initSubscriptionPayload.acId = unitHolder.acId;
        initSubscriptionPayload.hideUnitHolders = true;
      }

      //* Case: convert a debt into capital
      if (
        isSecurityTypeSTO(productDetail) &&
        productDetail.unitHolders?.length &&
        productDetail.sellPaymentMethod === "notPay"
      ) {
        productDetail.isDebtIntoCapitalProduct = true;
      }

      productDetail.sellMaxUnitOrigin = productDetail.sellMaxUnit;
      if (productDetail.ccy) {
        productDetail.ccy = isBahtDisplay(productDetail.ccy) ? undefined : productDetail.ccy.toUpperCase();
      }
      if (productDetail.paymentAccountCcy) {
        productDetail.paymentAccountCcy = isBahtDisplay(productDetail.paymentAccountCcy)
          ? undefined
          : productDetail.paymentAccountCcy.toUpperCase();
      }
      productDetail.paymentAccountCcy = productDetail.paymentAccountCcy ?? productDetail.ccy ?? undefined;

      yield put(getPrimaryProductDetailSuccess(productDetail));
      yield put(initSubscription(initSubscriptionPayload));

      if (productDetail.sellParValue !== productDetail.sellPriceUnit) {
        yield put(changeUnitDisplaySetting(UnitDisplay.UNIT));
      } else if (isSecurityTypeDBT(productDetail) || isSecurityTypeSB(productDetail)) {
        yield put(changeUnitDisplaySetting(productDetail.ccy ?? UnitDisplay.BAHT));
      }

      const redirectMethod = product.replaceUrl ? replace : push;
      yield put(redirectMethod(getRoutePath(Routes.Primary.ProductDetail, product.securityCode)));

      if (product.onSuccess) {
        product.onSuccess();
      }
    } else {
      yield put(getPrimaryProductDetailFailure({ message: `cancel some require disclaimer`, ignore: true }));
    }
  } catch (error) {
    console.log(error);
    switch (error?.code) {
      case "4501": // Out of time
        const substActiveStartTime = product.substActiveStartTime.replace(/(\d{2})(\d{2})/, `$1:$2`);
        const substActiveEndTime = product.substActiveEndTime.replace(/(\d{2})(\d{2})/, `$1:$2`);
        yield put(getPrimaryProductDetailFailure({ ...error, params: [substActiveStartTime, substActiveEndTime] }));
        break;

      case "4505": // Out of date
        const substStartDate = convertToThaiFormat(product.substStartDate, DateFormat_2);
        const substEndDate = convertToThaiFormat(product.substEndDate, DateFormat_2);
        yield put(getPrimaryProductDetailFailure({ ...error, params: [substStartDate, substEndDate] }));
        break;

      case "43948": // date limit
        const ageLimitDate = convertToThaiFormat(product.sellAgeLimitDate, DateFormat_1);
        yield put(getPrimaryProductDetailFailure({ ...error, params: [ageLimitDate] }));
        break;

      //* tax id validation
      case ErrorType.TAX_ID_MISSING_1:
      case ErrorType.TAX_ID_MISSING_2:
        if (isMissingTaxIdError(error?.code)) {
          error.ignore = true;
        }
        yield put(getPrimaryProductDetailFailure(error));
        break;

      default:
        yield put(getPrimaryProductDetailFailure(handleClientError(error)));
        break;
    }
  }
};

const saga = [
  takeLatest(getPrimaryProducts.type, getPrimaryProductsSaga),
  takeLatest(fetchBanner.type, fetchBannerSaga),
  takeLatest(getPrimaryProductDetail.type, getPrimaryProductDetailSaga),
];

export default saga;
